import { getOSToolsCatalog, type MCPTool, type OSTool } from '@/lib/toolsCatalog';

const STORAGE_OS_ENABLED = 'agent.tools.os.enabled.v1';
const STORAGE_MCP_ENABLED = 'agent.tools.mcp.enabled.v1';

function readSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.filter((x) => typeof x === 'string'));
  } catch {
    // ignore
  }
  return new Set();
}

function writeSet(key: string, set: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {
    // ignore
  }
}

export function isOSAppEnabledForAgent(appId: string): boolean {
  return readSet(STORAGE_OS_ENABLED).has(appId);
}

export function setOSAppEnabledForAgent(appId: string, enabled: boolean) {
  const s = readSet(STORAGE_OS_ENABLED);
  if (enabled) s.add(appId);
  else s.delete(appId);
  writeSet(STORAGE_OS_ENABLED, s);
  window.dispatchEvent(new CustomEvent('agent:tools-changed'));
}

export function isMCPServiceEnabledForAgent(serviceId: string): boolean {
  return readSet(STORAGE_MCP_ENABLED).has(serviceId);
}

export function setMCPServiceEnabledForAgent(serviceId: string, enabled: boolean) {
  const s = readSet(STORAGE_MCP_ENABLED);
  if (enabled) s.add(serviceId);
  else s.delete(serviceId);
  writeSet(STORAGE_MCP_ENABLED, s);
  window.dispatchEvent(new CustomEvent('agent:tools-changed'));
}

export function getSelectedToolsForAgent(params: { mcpServices?: { id: string; name: string; tier?: string; description?: string }[] } = {}) {
  const osAll = getOSToolsCatalog();
  const osEnabled = readSet(STORAGE_OS_ENABLED);
  const osTools: OSTool[] = osAll.filter((t) => osEnabled.has(t.id));

  const mcpEnabled = readSet(STORAGE_MCP_ENABLED);
  const mcpTools: MCPTool[] = (params.mcpServices || [])
    .filter((s) => mcpEnabled.has(s.id))
    .map((s) => ({
      id: s.id,
      category: 'mcp_service',
      name: s.name,
      tier: s.tier,
      description: s.description,
      samplePrompts: [],
    }));

  return {
    version: 'v1',
    selected: {
      osApps: osTools,
      mcpServices: mcpTools,
    },
    hints: {
      usage: 'Only use enabled tools. Respect agent file allowlist when interacting with files.',
    },
  };
}
